// # Chapter Stats Component
import React, { useState, useEffect } from "react";
import { Tabs, Tab } from "@mui/material";
import {
  FaChartBar,
  FaExclamationTriangle,
  FaSkull,
  FaArrowRight,
  FaClock,
} from "react-icons/fa";
import LibraryHeader from "../lib_components/lib_header";
import DashboardFooter from "../../../components/DashboardFooter";
import { doc, updateDoc, onSnapshot } from "firebase/firestore";
import { db } from "../../../auth/firebase";
import { useAuthStore } from "../../../auth/state";
import cacheManager from "../../../auth/cache_manager";
import "./chapter_stats1.css";
import { useNavigate } from "react-router-dom";
import ChapterDurationStats from "./chp_duartion_stats1";

// # Import Recharts components
import {
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  AreaChart,
  Area,
} from "recharts";

// # Import chapter icons
// import anatomyIcon from "./step1_icons/anatomy.png";
import biochemistryIcon from "./step1_icons/biochemistry.png";
import bloodIcon from "./step1_icons/blood.png";
import cnsIcon from "./step1_icons/cns.png";
import endocrineIcon from "./step1_icons/endocrine.png";
import eyeIcon from "./step1_icons/eye.png";
import gitIcon from "./step1_icons/git.png";
import heartIcon from "./step1_icons/heart.png";
import immunologyIcon from "./step1_icons/immunology.png";
import infectiousIcon from "./step1_icons/infectious.png";
import kidneyIcon from "./step1_icons/kidney.png";
import lungsIcon from "./step1_icons/lungs.png";
import musculoskeletalIcon from "./step1_icons/musculoskeletal.png";
import pathologyIcon from "./step1_icons/pathology.png";
import pharmaIcon from "./step1_icons/pharma.png";
import psychiatryIcon from "./step1_icons/psychiatry.png";
import publicHealthIcon from "./step1_icons/public_health.png";
import reproductiveIcon from "./step1_icons/reproductive-system.png";
import revisionIcon from "./step1_icons/revision.png";
import skinIcon from "./step1_icons/skin.png";

// # Import chapters and topics for dynamic counting
import { chapters } from "./library_step_1.js";
import { chapterTopics } from "./step1_chapter.js";

// # Custom TabPanel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`chpst1-tabpanel-${index}`}
      aria-labelledby={`chpst1-tab-${index}`}
      className="chpst1_tabpanel"
      {...other}
    >
      {value === index && children}
    </div>
  );
}

// # Calculate total topics from enabled chapters
const TOTAL_HARDCODED_TOPICS = chapters.reduce((total, chapter) => {
  if (chapter.enabled) {
    // # Special handling for chapters with naming inconsistencies
    let chapterSlug;
    if (chapter.title === "Hematology & Oncology") {
      chapterSlug = "hematology_and_oncology";
    } else if (chapter.title === "Musculoskeletal & Connective Tissue") {
      chapterSlug = "musculoskeletal_and_connective_tissue";
    } else {
      chapterSlug = chapter.title.toLowerCase().replace(/[\s&]+/g, "_");
    }
    return total + (chapterTopics[chapterSlug]?.length || 0);
  }
  return total;
}, 0);

// # Calculate total enabled chapters dynamically from the source of truth
const TOTAL_ENABLED_CHAPTERS = chapters.filter(
  (chapter) => chapter.enabled,
).length;

// # Chapter slugs from step1_chapter.js
// const CHAPTER_SLUGS = [
//   "biochemistry",
//   "immunology",
//   "microbiology",
//   "general_pathology",
//   "pharmacology",
//   "biostatistics",
//   "behavioral_science",
//   "cardiovascular",
//   "endocrine",
//   "gastrointestinal",
//   "hematology_oncology",
//   "musculoskeletal_connective_tissue",
//   "dermatology",
//   "neurology",
//   "special_senses",
//   "psychiatry",
//   "renal",
//   "reproductive",
//   "respiratory",
//   "rapid_review",
// ];

// # Pastel colors for charts
const CHART_COLORS = {
  easy: "#A8E6CF", // pastel green
  good: "#DCEDC1", // pastel yellow-green
  hard: "#FFD3B6", // pastel orange
  oh_no: "#FFAAA5", // pastel red
  primary: "#B6DCFE", // pastel blue
  secondary: "#D9D3FE", // pastel purple
  accent1: "#FFC1E3", // pastel pink
  accent2: "#C1FFF3", // pastel teal
  accent3: "#FFFFBE", // pastel yellow
};

// # Chapter icons mapping
const chapterIcons = {
  biochemistry: biochemistryIcon,
  immunology: immunologyIcon,
  microbiology: infectiousIcon,
  general_pathology: pathologyIcon,
  pharmacology: pharmaIcon,
  biostatistics: publicHealthIcon,
  behavioral_science: psychiatryIcon,
  cardiovascular: heartIcon,
  endocrine: endocrineIcon,
  gastrointestinal: gitIcon,
  hematology_oncology: bloodIcon,
  musculoskeletal_connective_tissue: musculoskeletalIcon,
  dermatology: skinIcon,
  neurology: cnsIcon,
  special_senses: eyeIcon,
  psychiatry: psychiatryIcon,
  renal: kidneyIcon,
  reproductive: reproductiveIcon,
  respiratory: lungsIcon,
  rapid_review: revisionIcon,
};

function ChapterStats() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [libraryData, setLibraryData] = useState(null);

  // # State for statistics data
  const [statsData, setStatsData] = useState({
    topicsCompleted: 0,
    totalTopics: TOTAL_HARDCODED_TOPICS,
    chaptersCompleted: 0,
    totalChapters: TOTAL_ENABLED_CHAPTERS,
    difficultyStats: {
      easy: 0,
      good: 0,
      hard: 0,
      oh_no: 0,
    },
    reviewCounts: [],
    chapterCompletion: [],
    topicsByChapter: [],
    recentActivity: [],
  });

  // # State for filtered topics
  const [hardTopics, setHardTopics] = useState([]);
  const [ohNoTopics, setOhNoTopics] = useState([]);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (
          userData &&
          userData.themes &&
          userData.themes.lib_theme !== undefined
        ) {
          setIsDarkMode(userData.themes.lib_theme === "dark");
        }
      }
    };

    loadThemePreference();
  }, [user]);

  // # Load stats data from Firestore
  useEffect(() => {
    if (!user?.uid) return;

    setLoading(true);

    const progressRef = doc(db, "my_lib_progress", user.uid);
    const unsubscribe = onSnapshot(progressRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        if (data.step1) {
          // # Calculate statistics
          const step1Data = data.step1;

          // # Filter enabled chapters (those with data)
          const availableChapters = Object.keys(step1Data);
          const enabledChapters = availableChapters.filter(
            (chapter) =>
              step1Data[chapter] && typeof step1Data[chapter] === "object",
          );

          // # Initialize variables
          let topicsCompleted = 0;
          let difficultyStats = { easy: 0, good: 0, hard: 0, oh_no: 0 };
          let reviewCounts = [];
          let chapterCompletion = [];
          let topicsByChapter = [];
          let allActivity = [];
          let chaptersCompleted = 0;

          // # Process each chapter
          enabledChapters.forEach((chapterSlug) => {
            const chapterData = step1Data[chapterSlug];

            // # Filter actual topic data (exclude metadata fields)
            const chapterTopics = Object.keys(chapterData).filter(
              (key) => key !== "iscomplete" && key !== "iscomplete_at",
            );

            // # Count completed topics
            const completedTopicsInChapter = chapterTopics.filter(
              (topic) => chapterData[topic]?.completed,
            ).length;

            // # Add to total topics count
            topicsCompleted += completedTopicsInChapter;

            // # Track chapter completion
            if (chapterData.iscomplete) {
              chaptersCompleted++;
            }

            // # Format user-friendly chapter name
            const chapterName = chapterSlug
              .split("_")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" ");

            // # Count topics by difficulty
            chapterTopics.forEach((topic) => {
              const topicData = chapterData[topic];
              if (topicData?.completed && topicData?.difficulty) {
                if (difficultyStats.hasOwnProperty(topicData.difficulty)) {
                  difficultyStats[topicData.difficulty]++;
                }
              }
            });

            // # Count reviews per chapter
            const chapterReviews = chapterTopics.reduce((sum, topic) => {
              return sum + (chapterData[topic]?.reviewTimestamps?.length || 0);
            }, 0);

            // # Add to reviews data for chart
            reviewCounts.push({
              name: chapterName,
              reviews: chapterReviews,
            });

            // # Add to chapter completion data for chart
            chapterCompletion.push({
              name: chapterName,
              completed: completedTopicsInChapter,
              total: chapterTopics.length,
              percentage:
                chapterTopics.length > 0
                  ? Math.round(
                      (completedTopicsInChapter / chapterTopics.length) * 100,
                    )
                  : 0,
            });

            // # Add to topics by chapter for chart
            topicsByChapter.push({
              name: chapterName,
              topics: chapterTopics.length,
            });

            // # Collect activity data
            chapterTopics.forEach((topic) => {
              const topicData = chapterData[topic];

              // # Add completion activity
              if (topicData?.completedAt) {
                allActivity.push({
                  date: new Date(topicData.completedAt),
                  type: "completion",
                  chapter: chapterName,
                  topic: topic,
                });
              }

              // # Add review activities
              if (
                topicData?.reviewTimestamps &&
                topicData.reviewTimestamps.length > 0
              ) {
                topicData.reviewTimestamps.forEach((timestamp) => {
                  allActivity.push({
                    date: new Date(timestamp),
                    type: "review",
                    chapter: chapterName,
                    topic: topic,
                  });
                });
              }
            });
          });

          // # Sort all activity by date (newest first) and take the 30 most recent
          allActivity.sort((a, b) => b.date - a.date);
          const recentActivity = allActivity.slice(0, 30).map((activity) => ({
            ...activity,
            dateFormatted: activity.date.toISOString().split("T")[0],
          }));

          // # Group recent activity by date for time series chart
          const activityByDate = {};
          recentActivity.forEach((activity) => {
            const date = activity.dateFormatted;
            if (!activityByDate[date]) {
              activityByDate[date] = { date, completions: 0, reviews: 0 };
            }

            if (activity.type === "completion") {
              activityByDate[date].completions++;
            } else if (activity.type === "review") {
              activityByDate[date].reviews++;
            }
          });

          // # Convert to array and sort by date
          const activitySeries = Object.values(activityByDate).sort(
            (a, b) => new Date(a.date) - new Date(b.date),
          );

          // # Update state with calculated statistics
          setStatsData({
            topicsCompleted,
            totalTopics: TOTAL_HARDCODED_TOPICS,
            chaptersCompleted,
            totalChapters: TOTAL_ENABLED_CHAPTERS,
            difficultyStats,
            reviewCounts,
            chapterCompletion,
            topicsByChapter,
            recentActivity: activitySeries,
          });

          setLoading(false);
        }
      } else {
        // # No data found
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [user]);

  // # Effect to filter topics by difficulty
  useEffect(() => {
    if (!user?.uid) return;

    const progressRef = doc(db, "my_lib_progress", user.uid);
    const unsubscribe = onSnapshot(progressRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        if (data.step1) {
          const step1Data = data.step1;
          const hardTopicsList = [];
          const ohNoTopicsList = [];

          // # Process each chapter
          Object.entries(step1Data).forEach(([chapterSlug, chapterData]) => {
            if (typeof chapterData === "object") {
              // # Filter actual topic data (exclude metadata fields)
              Object.entries(chapterData).forEach(([topicId, topicData]) => {
                if (
                  topicId !== "iscomplete" &&
                  topicId !== "iscomplete_at" &&
                  topicData?.completed
                ) {
                  // # Format chapter name
                  const chapterName = chapterSlug
                    .split("_")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ");

                  if (topicData.difficulty === "hard") {
                    hardTopicsList.push({
                      chapter: chapterSlug,
                      chapterName,
                      topicId,
                      topicName: topicId
                        .split("_")
                        .map(
                          (word) =>
                            word.charAt(0).toUpperCase() + word.slice(1),
                        )
                        .join(" "),
                      completedAt: topicData.completedAt,
                      reviewCount: topicData.reviewTimestamps?.length || 0,
                    });
                  } else if (topicData.difficulty === "oh_no") {
                    ohNoTopicsList.push({
                      chapter: chapterSlug,
                      chapterName,
                      topicId,
                      topicName: topicId
                        .split("_")
                        .map(
                          (word) =>
                            word.charAt(0).toUpperCase() + word.slice(1),
                        )
                        .join(" "),
                      completedAt: topicData.completedAt,
                      reviewCount: topicData.reviewTimestamps?.length || 0,
                    });
                  }
                }
              });
            }
          });

          // # Sort topics by completion date (newest first)
          setHardTopics(
            hardTopicsList.sort(
              (a, b) => new Date(b.completedAt) - new Date(a.completedAt),
            ),
          );
          setOhNoTopics(
            ohNoTopicsList.sort(
              (a, b) => new Date(b.completedAt) - new Date(a.completedAt),
            ),
          );
        }
      }
    });

    return () => unsubscribe();
  }, [user]);

  // # Load library data for duration stats
  useEffect(() => {
    if (!user?.uid) return;

    const progressRef = doc(db, "my_lib_progress", user.uid);
    const unsubscribe = onSnapshot(progressRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        setLibraryData(docSnapshot.data());
      }
    });

    return () => unsubscribe();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);

      if (user && user.uid) {
        await updateDoc(doc(db, "users", user.uid), {
          "themes.lib_theme": newTheme ? "dark" : "light",
        });

        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              lib_theme: newTheme ? "dark" : "light",
            },
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }

      // # Dispatch theme change event for footer
      const themeChangeEvent = new CustomEvent("theme_change", {
        detail: { isDark: newTheme },
      });
      window.dispatchEvent(themeChangeEvent);
    } catch (error) {
      console.error("Error updating theme preference:", error);
    }
  };

  // # Helper to handle hard topics tab selection
  const handleHardTopicsTab = () => {
    setActiveTab(1);
  };

  // # Helper to handle oh no topics tab selection
  const handleOhNoTopicsTab = () => {
    setActiveTab(2);
  };

  // # Handle topic navigation
  const handleTopicClick = (chapter, topicId) => {
    const isAdmin = window.location.pathname.startsWith("/admin");
    const basePath = isAdmin ? "/admin/library/step1" : "/library/step1";
    navigate(`${basePath}/${chapter}/topic/${topicId}`);
  };

  // # Convert the difficulty stats to an array for the pie chart
  const difficultyChartData = [
    {
      name: "Easy",
      value: statsData.difficultyStats.easy,
      color: CHART_COLORS.easy,
    },
    {
      name: "Good",
      value: statsData.difficultyStats.good,
      color: CHART_COLORS.good,
    },
    {
      name: "Hard",
      value: statsData.difficultyStats.hard,
      color: CHART_COLORS.hard,
    },
    {
      name: "Oh No",
      value: statsData.difficultyStats.oh_no,
      color: CHART_COLORS.oh_no,
    },
  ];

  // # Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="chpst1_custom-tooltip">
          <p className="chpst1_tooltip-label">{`${label}`}</p>
          {payload.map((entry, index) => (
            <p
              key={`item-${index}`}
              className="chpst1_tooltip-value"
              style={{ color: entry.color }}
            >
              {`${entry.name}: ${entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // # Custom tooltip for completion chart
  // const CompletionTooltip = ({ active, payload, label }) => {
  //   if (active && payload && payload.length) {
  //     const data = payload[0].payload;
  //     return (
  //       <div className="chpst1_custom-tooltip">
  //         <p className="chpst1_tooltip-label">{label}</p>
  //         <p className="chpst1_tooltip-value" style={{ color: "#4CAF50" }}>
  //           {`Completed: ${data.completed}/${data.total} (${data.percentage}%)`}
  //         </p>
  //       </div>
  //     );
  //   }
  //   return null;
  // };

  return (
    <div
      className={`chpst1_stats-container ${isDarkMode ? "chpst1_dark-mode" : ""}`}
    >
      <LibraryHeader
        isDarkMode={isDarkMode}
        onThemeToggle={handleThemeToggle}
      />

      <main className="chpst1_main-content">
        <div className="chpst1_header">
          <h1>Library Statistics</h1>
          <p>Track your progress and identify areas for improvement</p>
        </div>

        <div className="chpst1_tabs-wrapper">
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            aria-label="chapter statistics tabs"
            className="chpst1_tabs"
            TabIndicatorProps={{
              style: {
                backgroundColor: isDarkMode ? "#64B5F6" : "#2196F3",
              },
            }}
            variant="fullWidth"
          >
            <Tab
              icon={<FaChartBar />}
              label="Overview"
              className="chpst1_tab"
            />
            <Tab
              icon={<FaExclamationTriangle />}
              label="Hard Topics"
              className="chpst1_tab"
            />
            <Tab
              icon={<FaSkull />}
              label="Oh No Topics"
              className="chpst1_tab"
            />
            <Tab
              icon={<FaClock />}
              label="Duration Stats"
              className="chpst1_tab"
            />
          </Tabs>
        </div>

        <TabPanel value={activeTab} index={0}>
          {loading ? (
            <div className="chpst1_loading">
              <div className="chpst1_spinner"></div>
              <p>Loading statistics...</p>
            </div>
          ) : (
            <div className="chpst1_overview-content">
              <div className="chpst1_stats-summary">
                <div className="chpst1_stat-card">
                  <h3>Topics Completed</h3>
                  <div className="chpst1_stat-value-large">
                    {statsData.topicsCompleted}/{statsData.totalTopics}
                  </div>
                  <div className="chpst1_progress-bar">
                    <div
                      className="chpst1_progress-fill"
                      style={{
                        width: `${Math.round((statsData.topicsCompleted / statsData.totalTopics) * 100)}%`,
                      }}
                    ></div>
                  </div>
                  <div className="chpst1_progress-text">
                    {Math.round(
                      (statsData.topicsCompleted / statsData.totalTopics) * 100,
                    )}
                    % Complete
                  </div>
                </div>

                <div className="chpst1_stat-card">
                  <h3>Chapters Completed</h3>
                  <div className="chpst1_stat-value-large">
                    {statsData.chaptersCompleted}/{statsData.totalChapters}
                  </div>
                  <div className="chpst1_progress-bar">
                    <div
                      className="chpst1_progress-fill"
                      style={{
                        width: `${Math.round((statsData.chaptersCompleted / statsData.totalChapters) * 100)}%`,
                      }}
                    ></div>
                  </div>
                  <div className="chpst1_progress-text">
                    {Math.round(
                      (statsData.chaptersCompleted / statsData.totalChapters) *
                        100,
                    )}
                    % Complete
                  </div>
                </div>

                <div className="chpst1_stat-card">
                  <h3>By Difficulty</h3>
                  <div className="chpst1_difficulty-grid">
                    <div
                      className="chpst1_difficulty-item"
                      style={{ backgroundColor: `${CHART_COLORS.easy}40` }}
                    >
                      <span className="chpst1_difficulty-label">Easy</span>
                      <span className="chpst1_difficulty-count">
                        {statsData.difficultyStats.easy}
                      </span>
                    </div>
                    <div
                      className="chpst1_difficulty-item"
                      style={{ backgroundColor: `${CHART_COLORS.good}40` }}
                    >
                      <span className="chpst1_difficulty-label">Good</span>
                      <span className="chpst1_difficulty-count">
                        {statsData.difficultyStats.good}
                      </span>
                    </div>
                    <div
                      className="chpst1_difficulty-item"
                      style={{ backgroundColor: `${CHART_COLORS.hard}40` }}
                    >
                      <span className="chpst1_difficulty-label">Hard</span>
                      <span
                        className="chpst1_difficulty-count chpst1_clickable"
                        onClick={handleHardTopicsTab}
                      >
                        {statsData.difficultyStats.hard}
                      </span>
                    </div>
                    <div
                      className="chpst1_difficulty-item"
                      style={{ backgroundColor: `${CHART_COLORS.oh_no}40` }}
                    >
                      <span className="chpst1_difficulty-label">Oh No</span>
                      <span
                        className="chpst1_difficulty-count chpst1_clickable"
                        onClick={handleOhNoTopicsTab}
                      >
                        {statsData.difficultyStats.oh_no}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="chpst1_charts-grid">
                <div className="chpst1_chart-card">
                  <h3>Topics by Difficulty</h3>
                  <div className="chpst1_chart-container">
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={difficultyChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, percent }) =>
                            percent > 0
                              ? `${name}: ${(percent * 100).toFixed(0)}%`
                              : ""
                          }
                        >
                          {difficultyChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="chpst1_chart-card">
                  <h3>Recent Activity</h3>
                  <div className="chpst1_chart-container">
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart
                        data={statsData.recentActivity}
                        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Area
                          type="monotone"
                          dataKey="completions"
                          stackId="1"
                          stroke={CHART_COLORS.easy}
                          fill={CHART_COLORS.easy}
                          fillOpacity={0.6}
                          name="Completions"
                        />
                        <Area
                          type="monotone"
                          dataKey="reviews"
                          stackId="1"
                          stroke={CHART_COLORS.primary}
                          fill={CHART_COLORS.primary}
                          fillOpacity={0.6}
                          name="Reviews"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="chpst1_chart-card chpst1_chart-card-full">
                  <h3>Chapter Completion</h3>
                  <div className="chpst1_chart-container">
                    <ResponsiveContainer width="100%" height={400}>
                      <PieChart>
                        <Pie
                          data={[
                            {
                              name: "Completed",
                              value: statsData.chaptersCompleted,
                              color: CHART_COLORS.easy,
                            },
                            {
                              name: "Remaining",
                              value:
                                statsData.totalChapters -
                                statsData.chaptersCompleted,
                              color: CHART_COLORS.secondary,
                            },
                          ]}
                          cx="50%"
                          cy="50%"
                          labelLine={true}
                          outerRadius={150}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, percent }) =>
                            `${name}: ${(percent * 100).toFixed(0)}%`
                          }
                        >
                          {[
                            {
                              name: "Completed",
                              value: statsData.chaptersCompleted,
                              color: CHART_COLORS.easy,
                            },
                            {
                              name: "Remaining",
                              value:
                                statsData.totalChapters -
                                statsData.chaptersCompleted,
                              color: CHART_COLORS.secondary,
                            },
                          ].map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="chpst1_chart-details">
                    <div className="chpst1_completion-summary">
                      <div className="chpst1_completion-percentage">
                        {Math.round(
                          (statsData.chaptersCompleted /
                            statsData.totalChapters) *
                            100,
                        )}
                        % complete
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="chpst1_insights-section">
                <h3>Insights & Recommendations</h3>
                <div className="chpst1_insights-cards">
                  {statsData.topicsCompleted < statsData.totalTopics / 2 && (
                    <div className="chpst1_insight-card">
                      <h4>Progress Status</h4>
                      <p>
                        {`You've completed ${Math.round((statsData.topicsCompleted / statsData.totalTopics) * 100)}% of topics${
                          statsData.difficultyStats.hard +
                            statsData.difficultyStats.oh_no >
                          0
                            ? `, with ${statsData.difficultyStats.hard + statsData.difficultyStats.oh_no} topics marked as challenging`
                            : ""
                        }${
                          statsData.difficultyStats.easy > 0
                            ? ` and ${statsData.difficultyStats.easy} topics marked as easy`
                            : ""
                        }. ${
                          statsData.topicsCompleted < statsData.totalTopics / 2
                            ? "Consider focusing on completing more topics"
                            : statsData.difficultyStats.hard +
                                  statsData.difficultyStats.oh_no >
                                statsData.difficultyStats.easy
                              ? "Consider reviewing challenging topics before proceeding"
                              : "Keep up the good progress!"
                        }${
                          statsData.difficultyStats.hard +
                            statsData.difficultyStats.oh_no >
                          3
                            ? " and revisiting topics you find difficult."
                            : "."
                        }`}
                      </p>
                    </div>
                  )}

                  {statsData.topicsCompleted > statsData.totalTopics / 2 &&
                    statsData.topicsCompleted < statsData.totalTopics && (
                      <div className="chpst1_insight-card">
                        <h4>Progress Status</h4>
                        <p>
                          {`Great progress! You've completed ${Math.round((statsData.topicsCompleted / statsData.totalTopics) * 100)}% of topics${
                            statsData.difficultyStats.hard +
                              statsData.difficultyStats.oh_no >
                            0
                              ? `, with ${statsData.difficultyStats.hard + statsData.difficultyStats.oh_no} topics marked as challenging`
                              : ""
                          }. ${
                            statsData.difficultyStats.hard +
                              statsData.difficultyStats.oh_no >
                            statsData.difficultyStats.easy
                              ? "Focus on reviewing challenging topics while completing the remaining content"
                              : "Continue with this pace while maintaining your understanding of the content"
                          }${
                            statsData.difficultyStats.hard +
                              statsData.difficultyStats.oh_no >
                            3
                              ? " and consider revisiting difficult topics regularly."
                              : "."
                          }`}
                        </p>
                      </div>
                    )}

                  {statsData.topicsCompleted === statsData.totalTopics && (
                    <div className="chpst1_insight-card">
                      <h4>Progress Status</h4>
                      <p>
                        {`Excellent work! You've completed all topics${
                          statsData.difficultyStats.hard +
                            statsData.difficultyStats.oh_no >
                          0
                            ? `, with ${statsData.difficultyStats.hard + statsData.difficultyStats.oh_no} topics marked as challenging`
                            : ""
                        }. ${
                          statsData.difficultyStats.hard +
                            statsData.difficultyStats.oh_no >
                          statsData.difficultyStats.easy
                            ? "Focus on reviewing and mastering the challenging topics"
                            : "Maintain your knowledge by regular review sessions"
                        }${
                          statsData.difficultyStats.hard +
                            statsData.difficultyStats.oh_no >
                          3
                            ? " and consider creating a study plan for difficult topics."
                            : "."
                        }`}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <div className="chpst1_hard-topics-content">
            <h2>Hard Topics</h2>
            <p>Topics you've marked as challenging but manageable</p>
            {hardTopics.length > 0 ? (
              <div className="chpst1_difficulty-topics-grid">
                {hardTopics.map((topic, index) => (
                  <div
                    key={index}
                    className="chpst1_difficulty-topic-card"
                    onClick={() =>
                      handleTopicClick(topic.chapter, topic.topicId)
                    }
                  >
                    <div className="chpst1_difficulty-topic-header">
                      <div className="chpst1_difficulty-topic-icon">
                        <img
                          src={chapterIcons[topic.chapter]}
                          alt={topic.chapterName}
                        />
                      </div>
                      <div className="chpst1_difficulty-topic-content">
                        <div className="chpst1_difficulty-topic-chapter">
                          {topic.chapterName}
                        </div>
                        <div className="chpst1_difficulty-topic-name">
                          {topic.topicName}
                        </div>
                        <div className="chpst1_difficulty-topic-meta">
                          <span>Reviews: {topic.reviewCount}</span>
                          <span>
                            Completed:{" "}
                            {new Date(topic.completedAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="chpst1_difficulty-topic-arrow-container">
                      <FaArrowRight className="chpst1_difficulty-topic-arrow" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="chpst1_placeholder-text">
                <p>You don't have any topics marked as "Hard" yet.</p>
              </div>
            )}
          </div>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <div className="chpst1_oh-no-topics-content">
            <h2>Oh No Topics</h2>
            <p>Topics you're finding particularly challenging</p>
            {ohNoTopics.length > 0 ? (
              <div className="chpst1_difficulty-topics-grid">
                {ohNoTopics.map((topic, index) => (
                  <div
                    key={index}
                    className="chpst1_difficulty-topic-card"
                    onClick={() =>
                      handleTopicClick(topic.chapter, topic.topicId)
                    }
                  >
                    <div className="chpst1_difficulty-topic-header">
                      <div className="chpst1_difficulty-topic-icon">
                        <img
                          src={chapterIcons[topic.chapter]}
                          alt={topic.chapterName}
                        />
                      </div>
                      <div className="chpst1_difficulty-topic-content">
                        <div className="chpst1_difficulty-topic-chapter">
                          {topic.chapterName}
                        </div>
                        <div className="chpst1_difficulty-topic-name">
                          {topic.topicName}
                        </div>
                        <div className="chpst1_difficulty-topic-meta">
                          <span>Reviews: {topic.reviewCount}</span>
                          <span>
                            Completed:{" "}
                            {new Date(topic.completedAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="chpst1_difficulty-topic-arrow-container">
                      <FaArrowRight className="chpst1_difficulty-topic-arrow" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="chpst1_placeholder-text">
                <p>You don't have any topics marked as "Oh No" yet.</p>
              </div>
            )}
          </div>
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <ChapterDurationStats libraryData={libraryData} />
        </TabPanel>
      </main>

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default ChapterStats;
